import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { FileText, DollarSign, Building, CheckCircle, FolderOpen } from "lucide-react";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";
import api from "@/services/api";

const SUBSIDIARIES = [
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const CreateReceivableModal = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    client_name: "",
    invoice_number: "",
    amount: "",
    subsidiary: "",
    contract_reference: "",
    project_id: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch projects when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        client_name: "",
        invoice_number: "",
        amount: "",
        subsidiary: "",
        contract_reference: "",
        project_id: "",
        description: "",
      });
      setErrors({});
      fetchProjects();
    }
  }, [open]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get("/projects", {
        params: {
          page: 1,
          limit: 1000, // Get all projects
          status: "active", // Only active projects
        },
      });
      
      if (response.data.success) {
        setProjects(response.data.data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      // Don't show error toast, just log it
    } finally {
      setLoadingProjects(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.client_name.trim()) {
      newErrors.client_name = "Client name is required";
    }

    if (!formData.invoice_number.trim()) {
      newErrors.invoice_number = "Invoice number is required";
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than zero";
    }

    if (!formData.subsidiary) {
      newErrors.subsidiary = "Subsidiary is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // created_by references employees.id, not users.id
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const created_by = currentUser.Employee?.id || currentUser.id || 1;

      const receivableData = {
        client_name: formData.client_name.trim(),
        invoice_number: formData.invoice_number.trim(),
        amount: parseFloat(formData.amount),
        subsidiary: formData.subsidiary,
        contract_reference: formData.contract_reference.trim() || null,
        project_id: formData.project_id ? parseInt(formData.project_id) : null,
        description: formData.description.trim() || null,
        created_by,
      };

      await financeService.createReceivable(receivableData);
      onSuccess();
    } catch (error) {
      console.error("Error creating receivable:", error);
      toast.error(error.response?.data?.message || "Failed to create receivable");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="border-b pb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              Create Receivable
            </h2>
            <p className="text-gray-600 mt-1">
              Create a new accounts receivable entry for client invoicing
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client Name */}
            <div>
              <Label htmlFor="client_name" className="text-sm font-medium text-gray-700">
                Client Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="client_name"
                type="text"
                value={formData.client_name}
                onChange={(e) => handleChange("client_name", e.target.value)}
                className={`mt-1 ${errors.client_name ? "border-red-500" : ""}`}
                placeholder="Enter client name"
                maxLength={255}
              />
              {errors.client_name && (
                <p className="text-sm text-red-600 mt-1">{errors.client_name}</p>
              )}
            </div>

            {/* Invoice Number */}
            <div>
              <Label htmlFor="invoice_number" className="text-sm font-medium text-gray-700">
                Invoice Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="invoice_number"
                type="text"
                value={formData.invoice_number}
                onChange={(e) => handleChange("invoice_number", e.target.value)}
                className={`mt-1 ${errors.invoice_number ? "border-red-500" : ""}`}
                placeholder="INV-2024-001"
                maxLength={100}
              />
              {errors.invoice_number && (
                <p className="text-sm text-red-600 mt-1">{errors.invoice_number}</p>
              )}
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor="amount" className="text-sm font-medium text-gray-700">
                Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  className={`pl-10 ${errors.amount ? "border-red-500" : ""}`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && (
                <p className="text-sm text-red-600 mt-1">{errors.amount}</p>
              )}
            </div>

            {/* Subsidiary */}
            <div>
              <Label htmlFor="subsidiary" className="text-sm font-medium text-gray-700">
                Subsidiary <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.subsidiary}
                onValueChange={(value) => handleChange("subsidiary", value)}
              >
                <SelectTrigger className={`mt-1 ${errors.subsidiary ? "border-red-500" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-400" />
                    <SelectValue placeholder="Select subsidiary" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {SUBSIDIARIES.map((subsidiary) => (
                    <SelectItem key={subsidiary.value} value={subsidiary.value}>
                      {subsidiary.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.subsidiary && (
                <p className="text-sm text-red-600 mt-1">{errors.subsidiary}</p>
              )}
            </div>

            {/* Contract Reference (Optional) */}
            <div>
              <Label htmlFor="contract_reference" className="text-sm font-medium text-gray-700">
                Contract Reference (Optional)
              </Label>
              <Input
                id="contract_reference"
                type="text"
                value={formData.contract_reference}
                onChange={(e) => handleChange("contract_reference", e.target.value)}
                className="mt-1"
                placeholder="Contract or agreement reference"
                maxLength={255}
              />
            </div>

            {/* Project Link (Optional) */}
            <div>
              <Label htmlFor="project_id" className="text-sm font-medium text-gray-700">
                Project (Optional)
              </Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => handleChange("project_id", value)}
              >
                <SelectTrigger className="mt-1">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-gray-400" />
                    <SelectValue placeholder={loadingProjects ? "Loading projects..." : "Select project"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Link this receivable to a project for better tracking
              </p>
            </div>

            {/* Description (Optional) */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Add any additional details about this receivable..."
                rows={3}
                maxLength={500}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Create Receivable
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReceivableModal;
